import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from '../molecules/Tooltip';
import css from '../organisms/Table.css';
import MoreActionsPopover from '../../dataSources/components/MoreActionsPopover';
import Icon from '../foundations/Icon';
import CellLoader from './CellLoader';

const MAX_ACTIONS = 4;

const TableCellActions = ({
    actionComponentsConfigs = [],
    row = {},
    rowIndex = 0,
    containerStyle = {},
    extendedMenuTooltipText = '',
    isETL = false,
    className,
    dataTestId,
    isLoading = false,
    loaderClass = '',
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [showExtendedMenu, setShowExtendedMenu] = useState(false);

    const actionsToShow = actionComponentsConfigs.filter(({ shouldShow }) => shouldShow(row));
    const actionsWithIcons = actionsToShow
        .filter(action => !action.shouldHideIcon)
        .slice(0, MAX_ACTIONS)
        .reverse();
    const withKebabMenu = actionsWithIcons.length < actionsToShow.length;

    const handleLocation = event => {
        setAnchorEl(event.currentTarget);
    };

    const onOpenExtendedMenu = event => {
        if (event.currentTarget === anchorEl) {
            setAnchorEl(null);
            setShowExtendedMenu(false);
            return;
        }
        handleLocation(event);
        setShowExtendedMenu(true);
    };

    const onCloseExtendedMenu = () => {
        setShowExtendedMenu(false);
        setAnchorEl(null);
    };

    return (
        <td
            className={classNames(css.actions, className)}
            key={`${row.key || rowIndex}_actions`}
            style={{
                ...containerStyle,
            }}
        >
            <div>
                {isLoading ? (
                    <CellLoader loaderClass={loaderClass} />
                ) : (
                    <>
                        {!isETL && withKebabMenu && (
                            <>
                                <Tooltip
                                    title={extendedMenuTooltipText}
                                    key={`tooltip_more_${rowIndex}`}
                                    wrapperClass={classNames(css.action, css.extendedMenu)}
                                    style={{ float: 'right' }}
                                >
                                    <Icon
                                        name="dots-vertical"
                                        data-testid={dataTestId}
                                        key={`more_actions_${rowIndex}`}
                                        onClick={onOpenExtendedMenu}
                                    />
                                </Tooltip>
                                {showExtendedMenu && (
                                    <div className={css.popoverComponent}>
                                        <MoreActionsPopover
                                            showExtendedMenu={showExtendedMenu}
                                            onCloseExtendedMenu={onCloseExtendedMenu}
                                            anchorEl={anchorEl}
                                            actionComponentsConfigs={actionComponentsConfigs}
                                            row={row}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                        {actionsWithIcons.map(
                            ({
                                ActionComponent,
                                onClick,
                                onMouseEnter,
                                onMouseLeave,
                                key,
                                title,
                                tooltipText,
                                badge,
                                shouldTranslateToolTip,
                                actionComponentProps = {},
                            }) => (
                                <Tooltip
                                    open={tooltipText ? null : false}
                                    title={tooltipText}
                                    titleTranslationKey={shouldTranslateToolTip ? tooltipText : null}
                                    key={`tooltip_${key}`}
                                    wrapperClass={css.action}
                                >
                                    {badge && <span className={css.badge}>{badge}</span>}
                                    <ActionComponent
                                        {...actionComponentProps}
                                        title={title}
                                        key={`${row.key || rowIndex}_${key}_actions`}
                                        onClick={() => onClick(row)}
                                        onMouseEnter={onMouseEnter}
                                        onMouseLeave={onMouseLeave}
                                    />
                                </Tooltip>
                            )
                        )}
                    </>
                )}
            </div>
        </td>
    );
};

TableCellActions.propTypes = {
    actionComponentsConfigs: PropTypes.arrayOf(
        PropTypes.shape({
            ActionComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
            title: PropTypes.string,
            shouldShow: PropTypes.func,
            shouldHideIcon: PropTypes.bool,
        })
    ),
    row: PropTypes.shape({
        uanId: PropTypes.number,
    }),
    rowIndex: PropTypes.number,
    containerStyle: PropTypes.objectOf(PropTypes.any),
    extendedMenuTooltipText: PropTypes.string,
    isETL: PropTypes.bool,
    className: PropTypes.string,
    dataTestId: PropTypes.string,
    isLoading: PropTypes.bool,
    loaderClass: PropTypes.string,
};

export default TableCellActions;
